import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { FiLock } from "react-icons/fi";
import toast from "react-hot-toast";
import GameServices from "../../../services/GameServices";

const EMPTY = {
  duration_minutes: 3,
  total_ghar: 10,
  yantra_amount: 11,
  winning_amount: 100,
  difference_amount: 50,
  bet_close_seconds: 10,
  winner_animation: "default",
  sort_order: 0,
};

/**
 * Every number on this form is a whole rupee or a whole second, so the
 * decimal point never reaches state: the key is swallowed on the way in and
 * anything non-numeric is stripped from a paste. Same treatment the deposit
 * discount percentage already gets in Wallet settings.
 */
const BLOCKED_KEYS = [".", ",", "e", "E", "+", "-"];

const wholeNumberProps = (value, onChange) => ({
  type: "number",
  min: "0",
  step: "1",
  inputMode: "numeric",
  value,
  onChange: (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    onChange(digits === "" ? "" : Number(digits));
  },
  onKeyDown: (e) => {
    if (BLOCKED_KEYS.includes(e.key)) e.preventDefault();
  },
});

export default function GameFormModal({ show, game, options, onClose, onSaved }) {
  const gameApi = new GameServices();
  const isEdit = Boolean(game?.id);

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // The list of reveals comes from the server, so this dropdown and the
  // animations the client can actually play cannot drift apart.
  const [animations, setAnimations] = useState([]);

  useEffect(() => {
    if (!show) return;
    let alive = true;
    gameApi
      .getWinnerAnimations()
      .then((res) => {
        if (alive && res?.isSuccess && Array.isArray(res.data)) {
          setAnimations(res.data);
        }
      })
      .catch(() => {
        /* the form still saves; the dropdown just shows the stored value */
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setForm(
      isEdit
        ? {
            duration_minutes: Number(game.duration_minutes),
            total_ghar: Number(game.total_ghar),
            yantra_amount: Number(game.yantra_amount),
            winning_amount: Number(game.winning_amount),
            difference_amount: Number(game.difference_amount),
            bet_close_seconds: Number(game.bet_close_seconds),
            winner_animation: game.winner_animation || "default",
            sort_order: Number(game.sort_order),
          }
        : EMPTY,
    );
  }, [show, game, isEdit]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const durations = options?.durations || [];
  const minGhar = options?.min_ghar || 2;
  const maxGhar = options?.max_ghar || 16;

  const roundSeconds = Number(form.duration_minutes) * 60;
  const closePercent = roundSeconds
    ? Math.round((Number(form.bet_close_seconds) / roundSeconds) * 100)
    : 0;
  const roundsPerDay = form.duration_minutes ? Math.floor(1440 / form.duration_minutes) : 0;

  const gharOptions = [];
  for (let n = minGhar; n <= maxGhar; n += 1) gharOptions.push(n);

  // Mirrors deriveGameName / deriveGameCode on the server, so the form can
  // show what the game will be called before it is created.
  const derivedName = `${form.duration_minutes} Min`;
  const derivedCode = `${form.duration_minutes}min`;

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (Number(form.bet_close_seconds) >= roundSeconds) {
      toast.error("Bet close seconds must be shorter than the round itself");
      return;
    }

    setSaving(true);
    try {
      const res = isEdit
        ? await gameApi.updateGame({ ...form, id: game.id })
        : await gameApi.createGame(form);

      if (res?.isSuccess) {
        toast.success(res.message);
        onSaved(res?.data?.id || game?.id);
      } else {
        toast.error(res?.message || "Could not save the game");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save the game");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} centered backdrop="static" size="lg">
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">{isEdit ? `Edit ${game.game_name}` : "Add Game"}</h5>
        <button className="btn" onClick={onClose} disabled={saving} aria-label="Close">
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <form onSubmit={submit}>
        <Modal.Body>
          <div className="gm-form-grid">
            <div className="input-box">
              <label htmlFor="gf-duration">Round Duration</label>
              <select
                id="gf-duration"
                value={form.duration_minutes}
                disabled={isEdit}
                onChange={(e) => set("duration_minutes", Number(e.target.value))}
                autoFocus={!isEdit}
              >
                {durations.map((d) => (
                  <option key={d} value={d}>
                    {d} minute{d > 1 ? "s" : ""} — {Math.floor(1440 / d)} rounds/day
                  </option>
                ))}
              </select>
              {isEdit ? (
                <p className="gm-hint gm-hint-lock">
                  <FiLock size={11} /> Immutable.
                </p>
              ) : (
                <p className="gm-hint">
                  Only lengths that divide evenly into 24 hours are offered — {roundsPerDay} rounds
                  per day.
                </p>
              )}
            </div>

            <div className="input-box">
              <label htmlFor="gf-ghar">Total Ghar</label>
              <select
                id="gf-ghar"
                value={form.total_ghar}
                onChange={(e) => set("total_ghar", Number(e.target.value))}
              >
                {gharOptions.map((n) => (
                  <option key={n} value={n}>
                    {n} yantras
                  </option>
                ))}
              </select>
            </div>

            <div className="input-box">
              <label htmlFor="gf-yamt">Yantra Amount (bet unit)</label>
              <input id="gf-yamt" {...wholeNumberProps(form.yantra_amount, (v) => set("yantra_amount", v))} />
            </div>

            <div className="input-box">
              <label htmlFor="gf-wamt">Winning Amount</label>
              <input id="gf-wamt" {...wholeNumberProps(form.winning_amount, (v) => set("winning_amount", v))} />
            </div>

            <div className="input-box">
              <label htmlFor="gf-diff">Difference Winning Amount</label>
              <input id="gf-diff" {...wholeNumberProps(form.difference_amount, (v) => set("difference_amount", v))} />
            </div>

            <div className="input-box">
              <label htmlFor="gf-close">Bet Close Before End (seconds)</label>
              <input id="gf-close" {...wholeNumberProps(form.bet_close_seconds, (v) => set("bet_close_seconds", v))} />
              <p className={`gm-hint${closePercent >= 15 ? " gm-hint-warn" : ""}`}>
                {form.bet_close_seconds} s of a {roundSeconds} s round — operators get{" "}
                {Math.max(0, roundSeconds - Number(form.bet_close_seconds))} s to enter bets
                {closePercent >= 15 ? ` (${closePercent}% of the round)` : ""}.
              </p>
            </div>

            <div className="input-box">
              <label htmlFor="gf-anim">Winner Animation</label>
              <select
                id="gf-anim"
                className="form-select"
                value={form.winner_animation}
                onChange={(e) => set("winner_animation", e.target.value)}
              >
                {(animations.length
                  ? animations
                  : [{ slug: form.winner_animation, name: form.winner_animation }]
                ).map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.name}
                  </option>
                ))}
              </select>
              <p className="gm-hint">
                {animations.find((a) => a.slug === form.winner_animation)?.note ||
                  "Played over this game's closing seconds, when the winner is revealed."}
              </p>
            </div>

            <div className="input-box">
              <label htmlFor="gf-sort">Display Order</label>
              <input id="gf-sort" {...wholeNumberProps(form.sort_order, (v) => set("sort_order", v))} />
              <p className="gm-hint">Lower numbers show first in tabs and report columns.</p>
            </div>
          </div>

          {!isEdit ? (
            <p className="gm-note">
              This game will be named <b>{derivedName}</b> with the code{" "}
              <code className="gm-code-preview">{derivedCode}</code> — both come from the round
              length, so there is nothing to type. It starts stopped: fill all {form.total_ghar}{" "}
              yantra slots, then start it.
            </p>
          ) : null}
        </Modal.Body>

        <div className="modal-footer border-0">
          <button type="button" className="btn btn-light px-4" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn gm-btn-primary px-4" disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Game"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
